import { apiClient } from "./client"
import { encryptPassword } from "@/lib/auth/credential-crypto"

export type PermissionAction = "view" | "manage"
export type PermissionModule =
  | "leads"
  | "plans"
  | "roles"
  | "users"
  | "quotes"
  | "pricing"
  | "documents"
  | "activities"

export type PermissionsMap = Partial<
  Record<PermissionModule, Partial<Record<PermissionAction, boolean>>>
>

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  role_id?: string
  role_name?: string
  permissions?: PermissionsMap
  preferred_language: string
}

export interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
}

export interface LoginPayload {
  email: string
  password: string
}

export interface RegisterPayload {
  email: string
  name: string
  password: string
  phone?: string
  preferred_language?: "en" | "ar"
}

export interface AuthResponse {
  success: boolean
  message: string
  data: TokenPair & { user: AuthUser }
}

export interface RegisterResponse {
  success: boolean
  message: string
  data: AuthUser
}

export interface MeResponse {
  success: boolean
  data: AuthUser
}

export async function loginApi(payload: LoginPayload): Promise<AuthResponse> {
  const password = await encryptPassword(payload.password)
  const res = await apiClient.post<TokenPair & { user: AuthUser }>(
    "/auth/login",
    { ...payload, password }
  )
  return {
    success: true,
    message: "Login successful",
    data: res.data,
  }
}

export async function registerApi(
  payload: RegisterPayload
): Promise<RegisterResponse> {
  const password = await encryptPassword(payload.password)
  const res = await apiClient.post<AuthUser>("/auth/register", {
    ...payload,
    password,
  })
  return {
    success: true,
    message: "Registration successful",
    data: res.data,
  }
}

export async function refreshApi(
  refresh_token: string
): Promise<{ success: boolean; data: TokenPair }> {
  // Update auth token for this request
  const res = await apiClient.post<TokenPair>("/auth/refresh", {
    refresh_token,
  })
  return {
    success: true,
    data: res.data,
  }
}

export async function getMeApi(access_token: string): Promise<MeResponse> {
  // Set token for this request
  apiClient.setAuthToken(access_token)
  const res = await apiClient.get<AuthUser>("/auth/me")
  return {
    success: true,
    data: res.data,
  }
}

export async function updateProfileApi(payload: {
  name: string
}): Promise<{ success: boolean; data: AuthUser }> {
  const res = await apiClient.patch<AuthUser>("/auth/me", payload)
  return { success: true, data: res.data }
}

export async function logoutApi(access_token: string): Promise<void> {
  apiClient.setAuthToken(access_token)
  await apiClient.post<unknown>("/auth/logout", {})
}

export async function resendVerificationApi(email: string): Promise<void> {
  await apiClient.post<unknown>("/auth/resend-verification", { email })
}

export async function forgotPasswordApi(email: string): Promise<void> {
  await apiClient.post<unknown>("/auth/forgot-password", { email })
}

export async function validateResetTokenApi(
  token: string
): Promise<{ name: string; email: string }> {
  const res = await apiClient.post<{ name: string; email: string }>(
    "/auth/validate-reset-token",
    { token }
  )
  return res.data
}

export async function resetPasswordApi(payload: {
  token: string
  email: string
  password: string
}): Promise<void> {
  const password = await encryptPassword(payload.password)
  await apiClient.post<unknown>("/auth/reset-password", {
    ...payload,
    password,
  })
}

export async function verifyEmailApi(
  email: string,
  code: string
): Promise<AuthResponse> {
  const res = await apiClient.post<TokenPair & { user: AuthUser }>(
    "/auth/verify-email",
    { email, code }
  )
  return {
    success: true,
    message: "Email verified",
    data: res.data,
  }
}
